import {
  AvConnectionInfo,
  AvRefreshReasons,
  AvStateType,
  ConnectionQualityLevels,
  DeviceStateRepresentationType,
} from "@minervaproject/avsubsystem-core";

import { PaneType } from "./classroom";
import { HardwareStatsType } from "./hardware_monitor";
import { PreclassCheckResultType } from "./local";
import { BandwidthModeType } from "./user";

export enum LevelType {
  IMPORTANT = "IMPORTANT",
  INFO = "INFO",
  STATS = "STATS",
}

export enum MonitoringEventTypeType {
  ADMIN_CHANGE_AV_SERVICE = "admin-change-av-service",
  ADMIN_CHANGE_LP_TYPE = "admin-change-lp-type",
  ADMIN_CHANGE_MAX_UPDATED_VIDEOS = "admin-change-max-updated-videos",
  ADMIN_CHANGE_MAX_VIDEOS_IN_PARTICIPANT_BAR = "admin-change-max-videos-in-participant-bar",
  ADMIN_FORCE_AUDIO_ONLY = "admin-force-audio-only",
  ADMIN_FORCE_HIGH_BANDWIDTH = "admin-force-high-bandwidth",
  ADMIN_FORCE_LIVE_CAPTIONS_CHANGE = "admin-force-live-captions-change",
  ADMIN_FORCE_RELOAD_AV = "admin-force-reload-av",
  ADMIN_FORCE_RELOAD_PAGE = "admin-force-reload-page",
  ADMIN_HARD_RESET_CLASS = "admin-hard-reset-class",
  ADMIN_RELOAD_PAGE_FOR_EVERYBODY = "admin-reload-page-for-everybody",
  ADMIN_SOFT_RESET_CLASS = "admin-soft-reset-class",
  AV_CHECKIN = "av-checkin",
  AV_CONNECTION_QUALITY_CHANGED = "av-connection-quality-changed",
  AV_REFRESH = "av-refresh",
  BUG_REPORT_SUBMITTED = "bug-report-submitted",
  CHANGE_BANDWIDTH_MODE = "change-bandwidth-mode",
  CHANGE_CLASS_MODALITY = "change-class-modality",
  CHANGE_CLASS_PTT_MODE = "change-class-ptt-mode",
  DEVICES_ERROR = "devices-error",
  DEVICES_SELECTED = "devices-selected",
  ENTER_CLASS = "enter-class",
  ENTER_CLASS_NEW_TAB = "enter-class-new-tab",
  EXTENSION_STATS = "extension-stats",
  FAILED_API_REQUEST = "failed-api-request",
  FAILED_CONNECTION_ALERT = "failed-connection-alert",
  FORCED_BANDWIDTH_MODE_CHANGE = "forced-bandwidth-mode-change",
  HARDWARE_LOAD = "hardware-load",
  LEAVE_CLASS = "leave-class",
  LEAVE_CLASS_NEW_TAB = "leave-class-new-tab",
  OPEN_PRESENTATION_VIEW = "open-presentation-view",
  PING_TIMEOUT = "ping-timeout",
  POOR_CONNECTION_INDICATOR_CHANGED = "poor-connection-indicator-changed",
  PRECLASS_CHECK_FAIL = "preclass-check-fail",
  PRECLASS_CHECK_STARTED = "preclass-check-started",
  PRECLASS_CHECK_SUCCESS = "preclass-check-success",
  PUBSUB_CONNECTED = "pubsub-connected",
  PUBSUB_DISCONNECTED = "pubsub-disconnected",
  PUBSUB_REQUEST_SYNC = "pubsub-request-sync",
  PUBSUB_STATS = "pubsub-stats",
  RELOAD_STREAM = "reload-stream",
  SHOW_BREAKOUT_CONTROL_ROOM = "show-breakout-control-room",
  SHOW_BREAKOUT_OVERVIEW_ROOM = "show-breakout-overview-room",
  SHOW_BREAKOUT_ROOM = "show-breakout-room",
  SHOW_MAIN_CLASSROOM = "show-main-classroom",
  START_MANUAL_RECORDING = "start-manual-recording",
  STOP_MANUAL_RECORDING = "stop-manual-recording",
  STREAM_FAILED = "stream-failed",
  STREAM_STARTED = "stream-started",
  TIMELINE_SEGMENT = "timeline-segment",
  TIMELINE_SEGMENT_SECONDARY_ACTION = "timeline-segment-secondary-action",
  TOGGLE_BACKGROUND_BLUR = "toggle-background-blur",
  TOGGLE_CAMERA = "toggle-camera",
  TOGGLE_LIVE_CAPTIONS = "toggle-live-captions",
  TOGGLE_MIC = "toggle-mic",
}

export type contextDataKeyType =
  | "avService"
  | "avState"
  | "fullAvState"
  | "bandwidthMode"
  | "breakoutSessionId"
  | "canTogglePtt"
  | "channel"
  | "connectionQuality"
  | "data"
  | "devicesState"
  | "devicesError"
  | "enableLiveCaptions"
  | "enabled"
  | "hasFocus"
  | "hasPoorConnection"
  | "ipAddress"
  | "isMobile"
  | "isPresentationView"
  | "logsUrl"
  | "message"
  | "method"
  | "modality"
  | "mostCommonMsg"
  | "msgCount"
  | "newBwMode"
  | "oldBwMode"
  | "op"
  | "panes"
  | "ptt"
  | "preclassCheckResult"
  | "reason"
  | "rtt"
  | "sessionId"
  | "streamError"
  | "subject"
  | "submissionUrl"
  | "targetUserId"
  | "timelineSegmentId"
  | "urlPath"
  | "userAgent";

export type contextDataValueType =
  | string
  | boolean
  | number
  | object
  | undefined
  | null
  | FormData
  | AvStateType
  | AvConnectionInfo
  | PreclassCheckResultType
  | DeviceStateRepresentationType;

interface BaseContextDataType {
  avState?: AvStateType;
}

interface AdminChangeAvServiceType {
  type: MonitoringEventTypeType.ADMIN_CHANGE_AV_SERVICE;
  contextData: {
    avService: string;
  } & BaseContextDataType;
}

interface BugReportSubmittedType {
  type: MonitoringEventTypeType.BUG_REPORT_SUBMITTED;
  contextData: {
    subject: string;
    submissionUrl: string;
    logsUrl: string;
  } & BaseContextDataType;
}

interface EnterClassType {
  type: MonitoringEventTypeType.ENTER_CLASS;
  contextData: {
    userAgent: string;
    ipAddress: string;
    isPresentationView: boolean;
    enableLiveCaptions: boolean;
  } & BaseContextDataType;
}

interface EnterClassOtherTabType {
  type: MonitoringEventTypeType.ENTER_CLASS_NEW_TAB;
  contextData: {
    isPresentationView: boolean;
  } & BaseContextDataType;
}

interface LeaveClassType {
  type: MonitoringEventTypeType.LEAVE_CLASS;
  contextData: {
    reason: string;
    isPresentationView: boolean;
  } & BaseContextDataType;
}

interface LeaveClassOtherTabType {
  type: MonitoringEventTypeType.LEAVE_CLASS_NEW_TAB;
  contextData: {
    isPresentationView: boolean;
  } & BaseContextDataType;
}

interface OpenPresentationViewType {
  type: MonitoringEventTypeType.OPEN_PRESENTATION_VIEW;
  contextData: {
    panes: PaneType[];
  } & BaseContextDataType;
}

interface AvCheckInType {
  type: MonitoringEventTypeType.AV_CHECKIN;
  contextData: {
    publisherStats: AvConnectionInfo | null;
  } & BaseContextDataType;
}

interface AvConnectionQualityChangedType {
  type: MonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED;
  contextData: {
    connectionQuality: ConnectionQualityLevels;
  } & BaseContextDataType;
}

interface AvRefreshType {
  type: MonitoringEventTypeType.AV_REFRESH;
  contextData: {
    reason: AvRefreshReasons;
  } & BaseContextDataType;
}

interface ReloadStreamType {
  type: MonitoringEventTypeType.RELOAD_STREAM;
  contextData: {
    targetUserId: string;
  } & BaseContextDataType;
}

// In stream started we include all the AV state
interface StreamStartedType {
  type: MonitoringEventTypeType.STREAM_STARTED;
  contextData: {
    fullAvState: AvStateType | null;
  } & BaseContextDataType;
}

interface StreamFailedType {
  type: MonitoringEventTypeType.STREAM_FAILED;
  contextData: {
    targetUserId: string;
    isScreenshare: boolean;
    streamError: string;
  } & BaseContextDataType;
}

interface FailedApiRequestType {
  type: MonitoringEventTypeType.FAILED_API_REQUEST;
  contextData: {
    method: "POST" | "GET";
    urlPath: string;
    data: string | FormData;
    status: number | "network-error";
  } & BaseContextDataType;
}

interface FailedConnectionAlertType {
  type: MonitoringEventTypeType.FAILED_CONNECTION_ALERT;
  contextData: {
    reason: string;
  } & BaseContextDataType;
}

interface ForcedBandwidthModeChangeType {
  type: MonitoringEventTypeType.FORCED_BANDWIDTH_MODE_CHANGE;
  contextData: {
    oldBwMode: BandwidthModeType;
    newBwMode: BandwidthModeType;
  };
}

interface DevicesSelectedType {
  type: MonitoringEventTypeType.DEVICES_SELECTED;
  contextData: {
    devicesState: DeviceStateRepresentationType | null;
  } & BaseContextDataType;
}
interface DevicesErrorType {
  type: MonitoringEventTypeType.DEVICES_ERROR;
  contextData: {
    devicesState: DeviceStateRepresentationType | null;
  } & BaseContextDataType;
}

interface PingTimeoutType {
  type: MonitoringEventTypeType.PING_TIMEOUT;
  contextData: {
    channel: string;
    isPresentationView: boolean;
  } & BaseContextDataType;
}

interface PoorConnectionIndicatorChangedType {
  type: MonitoringEventTypeType.POOR_CONNECTION_INDICATOR_CHANGED;
  contextData: {
    hasPoorConnection: boolean;
  } & BaseContextDataType;
}

interface PreclassCheckResultMonitoringType {
  type:
    MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS | MonitoringEventTypeType.PRECLASS_CHECK_FAIL;
  contextData: PreclassCheckResultType;
}

interface PreclassCheckStartedType {
  type: MonitoringEventTypeType.PRECLASS_CHECK_STARTED;
  contextData: Record<never, never>;
}

interface PubsubConnectedType {
  type: MonitoringEventTypeType.PUBSUB_CONNECTED;
  contextData: {
    channel: string;
  } & BaseContextDataType;
}

interface PubsubDisconnectedType {
  type: MonitoringEventTypeType.PUBSUB_DISCONNECTED;
  contextData: {
    channel: string;
    code?: number;
    reason?: string;
  } & BaseContextDataType;
}

interface PubsubRequestSyncType {
  type: MonitoringEventTypeType.PUBSUB_REQUEST_SYNC;
  contextData: { message: string };
}

interface PubsubStatsType {
  type: MonitoringEventTypeType.PUBSUB_STATS;
  contextData: {
    msgCount: number;
    mostCommonMsg: string | undefined;
    pingRTT: {
      max: number;
      median: number | undefined;
      min: number;
    };
    rtt: {
      max: number;
      median: number | undefined;
      min: number;
      slowestMsg: string | undefined;
    };
  } & BaseContextDataType;
}

interface ShowBreakoutRoomType {
  type:
    | MonitoringEventTypeType.SHOW_BREAKOUT_ROOM
    | MonitoringEventTypeType.SHOW_BREAKOUT_CONTROL_ROOM
    | MonitoringEventTypeType.SHOW_BREAKOUT_OVERVIEW_ROOM;
  contextData: {
    breakoutSessionId: string;
    breakoutRoom?: string;
  } & BaseContextDataType;
}

interface ShowMainClassroomType {
  type: MonitoringEventTypeType.SHOW_MAIN_CLASSROOM;
  contextData: {
    isPresentationView: boolean;
    isMobile: boolean;
  } & BaseContextDataType;
}

interface StartStopManualRecordingType {
  type:
    MonitoringEventTypeType.START_MANUAL_RECORDING | MonitoringEventTypeType.STOP_MANUAL_RECORDING;
  contextData: {
    sessionId: string;
  } & BaseContextDataType;
}

type SupportActionTypeType =
  | MonitoringEventTypeType.ADMIN_FORCE_AUDIO_ONLY
  | MonitoringEventTypeType.ADMIN_FORCE_HIGH_BANDWIDTH
  | MonitoringEventTypeType.ADMIN_FORCE_RELOAD_AV
  | MonitoringEventTypeType.ADMIN_FORCE_RELOAD_PAGE
  | MonitoringEventTypeType.ADMIN_HARD_RESET_CLASS
  | MonitoringEventTypeType.ADMIN_RELOAD_PAGE_FOR_EVERYBODY
  | MonitoringEventTypeType.ADMIN_SOFT_RESET_CLASS;

interface AdminForceLiveCaptionsChangeType {
  type: MonitoringEventTypeType.ADMIN_FORCE_LIVE_CAPTIONS_CHANGE;
  contextData: {
    targetUserId: string | null;
    enableLiveCaptions: boolean;
  };
}

interface SupportActionType {
  type: SupportActionTypeType;
  contextData: {
    targetUserId: string | null;
  };
}

interface ChangeBandwidthModeType {
  type: MonitoringEventTypeType.CHANGE_BANDWIDTH_MODE;
  contextData: {
    bandwidthMode: string;
  } & BaseContextDataType;
}

interface ChangeClassModalityType {
  type: MonitoringEventTypeType.CHANGE_CLASS_MODALITY;
  contextData: {
    modality: string;
  };
}

interface ChangeClassPTTModeType {
  type: MonitoringEventTypeType.CHANGE_CLASS_PTT_MODE;
  contextData: {
    ptt: boolean;
  };
}

interface TimelineSegmentType {
  type: MonitoringEventTypeType.TIMELINE_SEGMENT;
  contextData: {
    timelineSegmentId: string;
  };
}

interface TimelineSegmentSecondaryActionType {
  type: MonitoringEventTypeType.TIMELINE_SEGMENT_SECONDARY_ACTION;
  contextData: {
    timelineSegmentId: string;
    op: string;
  };
}

interface ToggleBackgroundBlurType {
  type: MonitoringEventTypeType.TOGGLE_BACKGROUND_BLUR;
  contextData: {
    enabled: boolean;
  };
}

interface ToggleCameraType {
  type: MonitoringEventTypeType.TOGGLE_CAMERA;
  contextData: {
    enabled: boolean;
  };
}

interface ToggleLiveCaptionsType {
  type: MonitoringEventTypeType.TOGGLE_LIVE_CAPTIONS;
  contextData: {
    enableLiveCaptions: boolean;
  };
}

interface ToggleMicType {
  type: MonitoringEventTypeType.TOGGLE_MIC;
  contextData: {
    enabled: boolean;
    canTogglePtt: boolean;
  };
}

interface HardwareLoadType {
  type: MonitoringEventTypeType.HARDWARE_LOAD;
  contextData: {
    hardwareInfo: HardwareStatsType;
  };
}

interface ExtensionStatsType {
  type: MonitoringEventTypeType.EXTENSION_STATS;
  contextData: {
    hardwareInfo: HardwareStatsType;
  };
}

type MonitoringEventTypeAndContextDataType =
  | AvCheckInType
  | AvConnectionQualityChangedType
  | AvRefreshType
  | AdminForceLiveCaptionsChangeType
  | AdminChangeAvServiceType
  | DevicesSelectedType
  | DevicesErrorType
  | BugReportSubmittedType
  | EnterClassType
  | EnterClassOtherTabType
  | ExtensionStatsType
  | HardwareLoadType
  | FailedApiRequestType
  | FailedConnectionAlertType
  | ForcedBandwidthModeChangeType
  | LeaveClassType
  | LeaveClassOtherTabType
  | OpenPresentationViewType
  | PingTimeoutType
  | PoorConnectionIndicatorChangedType
  | PreclassCheckResultMonitoringType
  | PreclassCheckStartedType
  | PubsubConnectedType
  | PubsubDisconnectedType
  | PubsubRequestSyncType
  | PubsubStatsType
  | ReloadStreamType
  | ShowBreakoutRoomType
  | ShowMainClassroomType
  | StartStopManualRecordingType
  | StreamStartedType
  | StreamFailedType
  | SupportActionType
  | ChangeBandwidthModeType
  | ChangeClassModalityType
  | ChangeClassPTTModeType
  | TimelineSegmentType
  | TimelineSegmentSecondaryActionType
  | ToggleBackgroundBlurType
  | ToggleCameraType
  | ToggleLiveCaptionsType
  | ToggleMicType;

interface MonitoringEventBaseType {
  id: string;
  userId: string;
  timestamp: number;
  orgId: string;
  orgShortName: string;
  classType: string;
}

export type MonitoringEventType = MonitoringEventTypeAndContextDataType & MonitoringEventBaseType;

/**
 * Runtime-checkable subset of `MonitoringEventTypeType`.
 *
 * The enum is a strict superset of `MonitoringEventType["type"]` (the union of
 * variants in `MonitoringEventTypeAndContextDataType`): the three admin members
 * below have no variant interface and therefore no valid `contextData` shape.
 * Rejecting them at the API boundary keeps an unvalidated `req.body.type` from
 * silently producing a payload whose `type` has no matching variant.
 */
const MONITORING_EVENT_TYPES_WITH_VARIANTS: ReadonlySet<string> = new Set(
  Object.values(MonitoringEventTypeType).filter(
    (type) =>
      type !== MonitoringEventTypeType.ADMIN_CHANGE_LP_TYPE &&
      type !== MonitoringEventTypeType.ADMIN_CHANGE_MAX_UPDATED_VIDEOS &&
      type !== MonitoringEventTypeType.ADMIN_CHANGE_MAX_VIDEOS_IN_PARTICIPANT_BAR,
  ),
);

export const isMonitoringEventType = (type: string): type is MonitoringEventType["type"] =>
  MONITORING_EVENT_TYPES_WITH_VARIANTS.has(type);

export type AvCheckinMonitoringEventType = AvCheckInType & MonitoringEventBaseType;

export type AvRefreshMonitoringEventType = AvRefreshType & MonitoringEventBaseType;

export type BugReportSubmittedMonitoringEventType = BugReportSubmittedType &
  MonitoringEventBaseType;

export type EnterClassMonitoringEventType = EnterClassType & MonitoringEventBaseType;

export type PreClassCheckResultMonitoringEventType = PreclassCheckResultMonitoringType &
  MonitoringEventBaseType;

export type PubsubStatsMonitoringEventType = PubsubStatsType & MonitoringEventBaseType;

export type FailedConnectionMonitoringEventType = FailedConnectionAlertType &
  MonitoringEventBaseType;

export type DevicesSelectedMonitoringEventType = DevicesSelectedType & MonitoringEventBaseType;

export type SupportActionMonitoringEventType = (
  SupportActionType | AdminForceLiveCaptionsChangeType
) &
  MonitoringEventBaseType;

export type TargetUserMonitoringEventTypeType = SupportActionTypeType | "reload-stream";

export type TargetUserMonitoringEventType = (
  SupportActionType | AdminForceLiveCaptionsChangeType | ReloadStreamType
) &
  MonitoringEventBaseType;

export type MonitoringEventHistogramDataType = {
  binMin: number;
  binMax: number;
  nEvents: number;
};
